package data.storage

import com.russhwolf.settings.Settings
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Browser-specific implementation of SecureStorage
 * Uses localStorage with basic encoding for demo purposes
 * TODO: Implement proper encryption when cryptography library issues are resolved
 */
class BrowserSecureStorage : SecureStorage {
    
    private val settings = Settings()
    
    // Simple XOR encryption for demo - NOT secure for production!
    private fun simpleEncrypt(text: String): String {
        val key = "PRDashboardKey123"
        return text.mapIndexed { index, char ->
            (char.code xor key[index % key.length].code).toChar()
        }.joinToString("").let { 
            // Convert to base64-like encoding
            it.encodeToByteArray().joinToString("-") { byte -> byte.toString() }
        }
    }
    
    private fun simpleDecrypt(encoded: String): String {
        return try {
            val bytes = encoded.split("-").map { it.toInt().toByte() }.toByteArray()
            val text = bytes.decodeToString()
            val key = "PRDashboardKey123"
            text.mapIndexed { index, char ->
                (char.code xor key[index % key.length].code).toChar()
            }.joinToString("")
        } catch (e: Exception) {
            println("Failed to decrypt: ${e.message}")
            ""
        }
    }
    
    override suspend fun storeEncrypted(key: String, value: String) {
        try {
            withContext(Dispatchers.Default) {
                val encrypted = simpleEncrypt(value)
                settings.putString("encrypted_$key", encrypted)
                println("🔐 Stored encrypted value for key: $key")
            }
        } catch (e: Exception) {
            println("💥 Failed to store encrypted value for $key: ${e.message}")
            // Fallback to plain storage
            storePlain(key, value)
        }
    }
    
    override suspend fun getDecrypted(key: String): String? {
        return try {
            withContext(Dispatchers.Default) {
                println("🔍 SecureStorage: Looking for encrypted key: $key")
                val encryptedString = settings.getStringOrNull("encrypted_$key")
                
                if (encryptedString == null) {
                    println("❌ SecureStorage: No encrypted value found for key: $key")
                    return@withContext null
                }
                
                println("🔓 SecureStorage: Found encrypted value, decrypting...")
                val decrypted = simpleDecrypt(encryptedString)
                
                if (decrypted.isEmpty()) {
                    println("⚠️ SecureStorage: Decryption resulted in empty string for key: $key")
                    return@withContext null
                }
                
                println("✅ SecureStorage: Successfully decrypted value for key: $key (length: ${decrypted.length})")
                decrypted
            }
        } catch (e: Exception) {
            println("💥 SecureStorage: Failed to decrypt value for $key: ${e.message}")
            // Fallback to plain storage
            println("🔄 SecureStorage: Trying fallback to plain storage...")
            getPlain(key)
        }
    }
    
    override fun storePlain(key: String, value: String) {
        settings.putString(key, value)
        println("💾 Stored plain value for key: $key")
    }
    
    override fun getPlain(key: String): String? {
        return settings.getStringOrNull(key)
    }
    
    override fun remove(key: String) {
        settings.remove(key)
        settings.remove("encrypted_$key")
        println("🗑️ Removed value for key: $key")
    }
    
    override fun clear() {
        settings.clear()
        println("🧹 Cleared all stored values")
    }
}

/**
 * Create browser-specific secure storage
 */
actual fun createSecureStorage(): SecureStorage = BrowserSecureStorage()
